import React, { useCallback, useMemo } from 'react';
import CustomMarker from './LocationMarker';
import type { PotentialLocation } from '@/types/firestore';
import { useAppDispatch } from '@/store/hooks';
import {
    setActiveInfoWindowId,
} from '@/store/slices/mapUiSlice';
import { Timestamp } from 'firebase/firestore';

// --- Props Interface --- 
interface PotentialMarkersProps {
    locations: PotentialLocation[]; // Expect locations as a prop
}

const PotentialMarkers: React.FC<PotentialMarkersProps> = ({ locations }) => {
    const dispatch = useAppDispatch();

    // --- Handlers using dispatch --- 
    const handleMarkerClick = useCallback((id: string | undefined) => {
      if (id) {
        dispatch(setActiveInfoWindowId(id));
      }
    }, [dispatch]);

    // --- Memoize marker components based on props --- 
    const markerComponents = useMemo(() => {
      // Use the locations prop passed from MapPage
      return locations.map((location) => {
        // Skip rendering if no ID or coordinates
        if (!location.id || !location.coordinates) return null;

        // Construct pointProps from PotentialLocation
        // Ensure PointProps type compatibility - might need adjustment based on actual PointProps definition
        const pointProps = {
          id: location.id, // Add the id property
          name: location.name || 'Potential Location', // Default name
          address: location.address || 'No address', // Default address
          locationType: location.locationType || 'other', // Default type
          averageRating: location.averageRating, // Pass if exists
          ratingCount: location.ratingCount,     // Pass if exists
          drinkCount: location.drinkCount,         // Pass if exists
          // Convert Timestamp to milliseconds number for compatibility with PointProps
          lastSightingDate: location.lastSightingDate instanceof Timestamp ? 
            location.lastSightingDate.toMillis() : undefined,
        };
        
        return (
          <CustomMarker
            key={location.id}
            locationId={location.id}
            // Pass individual props required by LocationMarker
            pointProps={pointProps} 
            lat={location.coordinates.latitude}
            lng={location.coordinates.longitude}
            isPotential={true} // Mark as potential location
            onClick={() => handleMarkerClick(location.id)}
          />
        );
      });
    }, [locations, handleMarkerClick]); // Removed unused dependencies handlePotentialLocationUpdate, handleRemoveLocation for this useMemo

    return <>{markerComponents}</>;
};

export default React.memo(PotentialMarkers); 